<template>
  <div class="webeo-wrapper">
    <div class="webeo-container">
      <div class="webeo-header">
        <img
          src="../assets/webeo-logo.png"
          alt="Webeo company logo"
        >
      </div>

      <form
        v-if="!isAwaitingMfaAuth"
        class="login-controls"
      >
        <div class="inputs">
          <PortalInput
            v-model="form.username"
            label="Username"
            :disabled="loggingIn"
            :error="usernameValidation"
            data-test="login-username"
            @input="$v.form.username.$touch();"
          />
          <PortalInput
            v-model="form.password"
            label="Password"
            data-test="login-password"
            :disabled="loggingIn"
            :type="fieldType"
            :error="passwordValidation"
            :suffix-clickable="true"
            @input="$v.form.password.$touch()"
            @suffix-clicked="revealPassword()"
          >
            <font-awesome-icon
              :icon="icon"
              data-test="reveal-eye"
            />
          </PortalInput>
          <a
            class="forgotten-password"
            data-test="forgotten-password-link"
            @click="$router.push('/ForgottenPassword')"
          >
            Forgotten Password?
          </a>
        </div>

        <div class="login-actions">
          <button
            data-test="login-submit"
            :disabled="loggingIn"
            @click.prevent="submitLogin();"
          >
            <transition
              name="fade"
              mode="out-in"
            >
              <div
                v-if="!loggingIn"
                key="login"
              >
                {{ $t('login.loginButton').toUpperCase() }}
              </div>
              <div
                v-else
                key="loading"
                class="loader-container"
              >
                <div class="loader">
                  Loading...
                </div>
              </div>
            </transition>
          </button>

          <div class="login-error">
            {{ errorMessage }}
          </div>
        </div>
      </form>

      <hr>

      <div
        v-if="!isAwaitingMfaAuth"
        class="demo-footer"
      >
        <div class="customer-text">
          <strong>{{ $t('login.notACustomer') }}</strong>
        </div>

        <span>{{ $t('login.getInTouch') }}</span>

        <button
          data-test="learn-more-button"
          :error="passwordValidation"
          @click="redirectToContact"
        >
          {{ $t('login.learnMore').toUpperCase() }}
        </button>
      </div>

      <MfaForm
        v-if="isAwaitingMfaAuth"
        :auth-session-id="authSessionId"
      />
    </div>

    <SupportDetails />
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapActions } from 'vuex';
import PortalInput from '../components/PortalInput.vue';
import SupportDetails from '../components/SupportDetails.vue';
import MfaForm from '../components/MfaForm.vue';

export default {
  components: {
    PortalInput,
    SupportDetails,
    MfaForm,
  },
  data: () => ({
    form: {
      username: null,
      password: null,
    },
    errorMessage: null,
    icon: 'eye-slash',
    fieldType: 'password',
    loggingIn: false,
    isAwaitingMfaAuth: false,
    authSessionId: null,
  }),
  validations: {
    form: {
      username: {
        required,
      },
      password: {
        required,
      },
    },
  },
  computed: {
    usernameValidation() {
      if (this.$v.form.username.$error && !this.$v.form.username.required) {
        return this.$t('login.validation.username.required');
      }

      return null;
    },
    passwordValidation() {
      if (this.$v.form.password.$error && !this.$v.form.password.required) {
        return this.$t('login.validation.password.required');
      }

      return null;
    },
  },
  methods: {
    ...mapActions(['login']),
    async submitLogin() {
      try {
        this.loggingIn = true;
        this.errorMessage = '';

        this.$v.$touch();
        if (this.$v.$invalid) {
          return;
        }

        const {
          success,
          isMfaEnabled,
          sessionId,
          errType,
        } = await this.login(this.form);

        if (success && isMfaEnabled) {
          this.isAwaitingMfaAuth = true;
          this.authSessionId = sessionId;
        } else if (success) {
          const returnUrl = this.$route.query.ReturnUrl || '/';
          await this.$router.push(returnUrl);
        } else {
          switch (errType) {
          case 'COMM_ERROR':
            this.errorMessage = 'There was an error connecting to Webeo. Please contact support.';
            break;
          case 'UNAUTHORISED':
            this.errorMessage = 'Invalid login attempt';
            break;
          case 'FORBIDDEN':
            this.errorMessage = 'Your account has been temporarily locked out. Please try again in 2 minutes.';
            break;
          default:
            this.errorMessage = 'An error occured. Please contact support.';
            break;
          }
        }
      } catch (err) {
        this.errorMessage = 'An error occured. Please contact support.';
      } finally {
        this.loggingIn = false;
      }
    },
    redirectToContact() {
      window.location.href = 'https://www.webeo.com/contact-us/';
    },
    revealPassword() {
      if (this.icon === 'eye-slash') {
        this.icon = 'eye';
        this.fieldType = 'text';
      } else {
        this.icon = 'eye-slash';
        this.fieldType = 'password';
      }
    },
  },
};
</script>

<style scoped>
.webeo-wrapper {
  position: relative;
  top: 5%;
}

.webeo-container * {
  transition: 0.2s ease-in-out;
}
.webeo-container {
  width: 650px;
  border: 1px solid var(--webeo-grey100);
  margin:0 auto;
  background-color: var(--webeo-white);
}
.dark-theme .webeo-container {
  border: 1px solid var(--webeo-white);
  background-color: var(--webeo-background-2);
}

.webeo-header {
  padding: 30px 0px 20px 0px;
  display: flex;
  justify-content: center;
}
.webeo-header img {
  width: 200px;
  margin-right: 70px;
}

.inputs {
  margin-top: 20px;
}
.dark-theme .inputs .portal-input-section > input {
  background-color: var(--webeo-foreground);
}

.forgotten-password {
  position: relative;
  float: right;
  margin-top: -15px;
  color: var(--webeo-cyan);
  text-decoration: underline;
}

.forgotten-password:hover {
  cursor: pointer;
  text-decoration: none;
}

.login-controls {
  padding: 0px 75px;
}
.login-actions {
  margin-bottom: 25px;
  position: relative;
}
.login-actions button {
  width: 100%;
  height: 40px;
  border-radius: 5px;
  margin-top: 10px;
  border: none;
  background-color: var(--webeo-magenta);
  color: var(--webeo-white);
  text-transform: uppercase;
  font-size: 16px;
  cursor: pointer;
}
.login-actions button:hover {
  background-color: var(--webeo-magenta-hover);
}
.login-actions button:focus {
  outline: none;
}
.login-actions button:disabled {
  background-color: var(--webeo-disabled);
  cursor: auto;
}

hr {
  margin: 20px 0px 20px 0px;
  border-color: var(--webeo-grey100);
  color: var(--webeo-grey100);
  background-color: var(--webeo-grey100);
  border: 0;
  height: 1px;
}

.demo-footer {
  padding: 0px 75px 25px 75px;
}
.demo-footer button {
  width: 140px;
  border-radius: 5px;
  height: 40px;
  border: 1px solid var(--webeo-grey100);
  margin-top: 10px;
  cursor: pointer;
}
.demo-footer button:hover {
  background-color: var(--webeo-grey150);
  border: 1px solid var(--webeo-grey170);
}
.demo-footer button:focus {
  outline: none;
}

.dark-theme .demo-footer button {
  background-color: var(--webeo-background-3);
  border-color: var(--webeo-background-3);
  color: var(--webeo-white);
}
.dark-theme .demo-footer button:hover {
  background-color: var(--webeo-accent-background-2);
  border-color: var(--webeo-accent-background-2);
}

.customer-text {
  padding-bottom: 10px;
  font-size: 15px;
}

.login-error {
  position: absolute;
  top: 100%;
  left: 0;
  display: inline-block;
  color: var(--webeo-error);
}

.loader,
.loader:before,
.loader:after {
  border-radius: 50%;
  width: 2.5em;
  height: 2.5em;
  animation: load7 1.8s infinite ease-in-out;
  animation-fill-mode: both;
  -webkit-animation: load7 1.8s infinite ease-in-out;
  -webkit-animation-fill-mode: both;
}
.loader {
  color: var(--webeo-white);
  font-size: 5px;
  margin: -17px auto;
  position: relative;
  text-indent: -9999em;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-animation-delay: -0.16s;
  animation-delay: -0.16s;
}
.loader:before,
.loader:after {
  content: '';
  position: absolute;
  top: 0;
}
.loader:before {
  left: -3.5em;
  -webkit-animation-delay: -0.32s;
  animation-delay: -0.32s;
}
.loader:after {
  left: 3.5em;
}
@-webkit-keyframes load7 {
  0%,
  80%,
  100% {
    box-shadow: 0 2.5em 0 -1.3em;
  }
  40% {
    box-shadow: 0 2.5em 0 0;
  }
}
@keyframes load7 {
  0%,
  80%,
  100% {
    box-shadow: 0 2.5em 0 -1.3em;
  }
  40% {
    box-shadow: 0 2.5em 0 0;
  }
}

.fade-enter-active, .fade-leave-active {
    transition: opacity .15s
}
.fade-enter, .fade-leave-to {
    opacity: 0
}
</style>
